// OPDReportPDF.jsx
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { parseDocument } from "htmlparser2";

// -------------------------------------------
// STYLES
// -------------------------------------------
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: "Helvetica",
    position: "relative",
    paddingTop: 130, // space for fixed header
    paddingBottom: 90, // space for fixed footer
    paddingHorizontal: 20,
  },

  header: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 60,
    padding: 18,
    backgroundColor: "#fff",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
  },

  logo: {
    width: 140,
    height: 30,
  },

  backgroundGray: {
    backgroundColor: "#f2f2f2",
    paddingVertical: 6,
    paddingHorizontal: 6,
    marginBottom: 0,
    borderRadius: 4,
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  infoItem: {
    width: "25%",
  },

  label: {
    fontWeight: 600,
    fontSize: 11,
  },

  horizontalLine: {
    borderBottomWidth: 0.6,
    borderBottomColor: "#ccc",
    marginTop: 5,
    marginBottom: 5,
  },

  box: {
    borderWidth: 1,
    borderColor: "#ccc",
    padding: 6,
    marginTop: 4,
    minHeight: 60,
  },

  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginTop: 10,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableHeader: {
    backgroundColor: "#d3d3d3",
  },
  tableCell: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#d3d3d3",
    padding: 4,
    fontSize: 9,
    flexGrow: 1,
    textAlign: "center",
  },
  bold: { fontWeight: "bold" },
  stripedRow: { backgroundColor: "#f2f2f2" },

  followupRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 14,
    paddingTop: 10,
    borderTopWidth: 0.5,
    borderTopColor: "#ccc",
  },

  followupLeft: { width: "50%" },
  followupRight: { width: "50%", textAlign: "right" },
  imageRight: { width: "50%", alignContent: "right" },
  followupLabel: { fontSize: 10, color: "#555" },
  followupDate: { fontSize: 11, fontWeight: "bold", marginTop: 2 },

  doctorName: { fontSize: 10, fontWeight: "bold" },
  doctorDetails: { fontSize: 9, marginTop: 2 },
  signatureText: {
    fontSize: 9,
    marginTop: 10,
    color: "#555",
    textDecoration: "underline",
  },

  footer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 60,
    backgroundColor: '#d3d3d3',
    padding: 10,
    borderTopWidth: 1,
    borderTopColor: "#ccc",
  },

  footer_white: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 60,
    // backgroundColor: '#d3d3d3',
    padding: 10,
    // borderTopWidth: 1,
    // borderTopColor: "#ccc",
  },

  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  footerColumn: {
    flexDirection: "column",
    alignItems: "center",
    width: "33%",
    fontSize: 9,
  },

  footerSmallCol: {
    width: "10%",
  },

  centerText: {
    textAlign: "center",
    marginTop: 6,
    fontSize: 9,
  },

  footer_logo: {
    marginLeft: 8,
    width: 16,
    height: 16,
  },

  spacer: {
    height: 20,
  },

  repeatHeader: {
    position: "absolute",
    top: 60,  // below the logo header (will be adjusted dynamically)
    left: 0,
    right: 0,
    paddingHorizontal: 20,
  },
});

// -------------------------------------------
// HELPERS
// -------------------------------------------
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const options = { month: "short", day: "2-digit", weekday: "long" };
  return date.toLocaleDateString("en-US", options);
};

const timeFormat = (time) => {
  if (!time) return "";
  return new Date(time).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

// DD-MM-YYYY, used for the OPD Date column in the header.
const formatDateDMY = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const pad = (n) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// H:i, used for the OPD Time column in the header.
const formatTimeHI = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const pad = (n) => String(n).padStart(2, "0");
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${hours}:${minutes}`;
};

const renderHtmlToPdf = (htmlString) => {
  if (!htmlString) return "";
  const doc = parseDocument(htmlString);
  const nodes = doc.children || [];

  const renderNodes = (nodesArray) =>
    nodesArray
      .map((node) => {
        if (node.type === "text") {
          const text = node.data.replace(/\s+/g, " ");
          return text.trim() ? text : null;
        }
        if (node.type === "tag") {
          const children = node.children ? renderNodes(node.children) : [];
          const tag = node.name.toLowerCase();

          switch (tag) {
            case "p":
              return [...children, "\n"];
            case "br":
              return "\n";
            case "strong":
            case "b":
              return children;
            case "em":
            case "i":
              return children;
            case "ul":
            case "ol": {
              const isOrd = tag === "ol";
              const lis = node.children.filter((c) => c.name === "li");
              return lis
                .map((li, idx) => [
                  isOrd ? `${idx + 1}. ` : "\u2022 ",
                  ...renderNodes(li.children),
                  "\n",
                ])
                .flat();
            }
            default:
              return children;
          }
        }
        return null;
      })
      .flat()
      .filter(Boolean);

  return renderNodes(nodes);
};

// -------------------------------------------
// MAIN PDF COMPONENT
// -------------------------------------------
const OPDReportPDF = ({ data }) => {
  const safe = {
    ...data,
    vital_data: data?.vital_data || {},
    pms_data: data?.pms_data || {},
    diagnosis_data: data?.diagnosis_data || [],
    red_alert: data?.red_alert || [],
    medication_data: data?.medication_data || [],
    investigation_data: data?.investigation_data || [],
    imaging_data: data?.imaging_data || [],
    def_diagnosis_data: data?.def_diagnosis_data || [],
    treatment_procedure_data: data?.treatment_procedure_data || [],
    treatment_other_data: data?.treatment_other_data || [],
    treatment_medication: data?.treatment_medication || [],
    pms_data: data?.pms_data || {},
  };

  // Check if doctor_id is 112 and clinic_id is 76 for custom header/footer
  const useCustomHeaderFooter = safe?.doctor_id === 112 && safe?.clinic_id === 76;
  // const customHeaderUrl = "https://nivaancare-asset.s3.ap-south-1.amazonaws.com/vidula_srv_header.png";
  const customHeaderUrl = "https://nivaancare-asset.s3.ap-south-1.amazonaws.com/header_vidula_srv.png";
  const customFooterUrl = "https://nivaancare-asset.s3.ap-south-1.amazonaws.com/vidula_srv_footer.png";

  return (
    <Document>
      <Page size="A4" style={{...styles.page, paddingTop: useCustomHeaderFooter ? 180 : 140}}>
        {/* ---------------- HEADER (FIXED) ---------------- */}
        <View style={useCustomHeaderFooter ? { position: 'absolute', top: 0, left: 0, right: 0, height:130 } : styles.header} fixed>
          {useCustomHeaderFooter ? (
            <Image 
              style={{ width: '100%', height: '100%' }} 
              src={customHeaderUrl} 
            />
          ) : (
            <>
              {safe?.primary_logo ? 
              <Image style={styles.logo} src={safe?.primary_logo} />
              :
              <Image style={styles.logo} src="/Nivaan_logo.png" />
              }
              
              {safe?.sec_logo ?
                <>
                  {safe?.clinic_meta_data?.sec_logo_style == false ?
                    <Image
                        src={safe?.sec_logo}
                        alt="logo"
                        // style={styles.logo}
                      />
                  :
                    <Image
                      src={safe?.sec_logo}
                      alt="logo"
                      style={styles.logo}
                    />
                  
                  }
                </>
                
              :
               null
              }
            </>
          )}
        </View>
        {/* {"sec_logo_style":false,"footer":"mss"} */}
        {/* ---------------- REPEATING BASIC-INFO HEADER (FIXED) ---------------- */}
        <View style={[styles.backgroundGray, {
          position: "absolute",
          top: useCustomHeaderFooter ? 130 : 60,
          left: 0,
          right: 0,
          paddingHorizontal: 20,
        }]} fixed wrap={false}>
          <View style={styles.row}>
            <Text style={[styles.infoItem, { width: "33%" }]}>
              <Text style={styles.label}>Name: </Text>
              {safe.full_name}
            </Text>
            <Text style={[styles.infoItem, { width: "24%", paddingLeft: 5 }]}>
              <Text style={styles.label}>Age: </Text>
              {safe.age}
            </Text>
            <Text style={[styles.infoItem, { width: "21%" }]}>
              <Text style={styles.label}>Gender: </Text>
              {safe.gender}
            </Text>
            <Text style={[styles.infoItem, { width: "22%" }]}>
              <Text style={styles.label}>UHID: </Text>
              {safe.patient_id}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={[styles.infoItem, { width: "33%" }]}>
              <Text style={styles.label}>Doctor: </Text>
              {safe.doctor_name}
            </Text>
            <Text style={[styles.infoItem, { width: "24%", paddingLeft: 5 }]}>
              <Text style={styles.label}>OPD Date: </Text>
              {formatDateDMY(safe.adjusted_time)}
            </Text>
            <Text style={[styles.infoItem, { width: "21%" }]}>
              <Text style={styles.label}>OPD Time: </Text>
              {formatTimeHI(safe.adjusted_time)}
            </Text>
            <Text style={[styles.infoItem, { width: "22%" }]}>
              <Text style={styles.label}>Mode: </Text>In-Person
            </Text>
          </View>
        </View>

        {/* ------------------------------------------------------ */}
        {/* 1. BASIC INFO (LOCKED)                                */}
        {/* ------------------------------------------------------ */}
        {/* <View style={styles.backgroundGray} wrap={false}>
          <View style={styles.row}>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Name: </Text>
              {safe.full_name}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Age: </Text>
              {safe.age}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Gender: </Text>
              {safe.gender}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Nivaan UHIDs: </Text>
              {safe.patient_id}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>OPD Date: </Text>
              {formatDate(safe.adjusted_time)}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>OPD Time: </Text>
              {timeFormat(safe.adjusted_time)}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>OPD Type: </Text>PMS Consult
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Mode: </Text>In-Person
            </Text>
          </View>
        </View> */}

        {/* ------------------------------------------------------ */}
        {/* 2. VITALS (LOCKED)                                    */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: useCustomHeaderFooter ? 20 : 0 }}>
          <View style={styles.row}>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Height: </Text>
              {safe.vital_data.height}' {safe.vital_data.height_inch}"
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Weight: </Text>
              {safe.vital_data.weight} kgs
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>BMI: </Text>
              {safe.vital_data.bmi}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Pulse: </Text>
              {safe.vital_data.pulse}
            </Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>BP: </Text>
              {safe.vital_data.bp}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Respiration/min: </Text>
              {safe.vital_data.respiration}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Temperature in °F: </Text>
              {safe.vital_data.temperature}
            </Text>
            <Text style={styles.infoItem}>
              <Text style={styles.label}>Pain Score: </Text>
              {safe.pms_data.vas_score}
            </Text>
          </View>
        </View>

        {/* ------------------------------------------------------ */}
        {/* 3. DIAGNOSIS (LOCKED)                                 */}
        {/* ------------------------------------------------------ */}
        <View wrap={false}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>
            {safe.pms_data?.is_final ? "Final Diagnosis:" : "Provisional Diagnosis:"}
          </Text>
          {safe.diagnosis_data.map((d, i) => (
            <Text key={i}>
              {d?.diagnosis || d?.provisionaldiagnosis}
              {d.remark ? ` - ${d.remark}` : ""}
            </Text>
          ))}
        </View>

        {/* ------------------------------------------------------ */}
        {/* DIFFERENTIAL DIAGNOSIS                                  */}
        {/* ------------------------------------------------------ */}
        {safe.def_diagnosis_data?.length > 0 && (
          <View wrap={false} style={{ marginTop: 10 }}>
            <View style={styles.horizontalLine} />
            <Text style={styles.label}>Differential Diagnosis :</Text>
            {safe.def_diagnosis_data.map((d, i) => (
              <Text key={i}>
                {d?.diagnosis || d?.differentialdiagnosis}
                {d.remark ? ` - ${d.remark}` : ""}
              </Text>
            ))}
          </View>
        )}

        {/* ------------------------------------------------------ */}
        {/* 4. RED FLAGS (LOCKED)                                 */}
        {/* ------------------------------------------------------ */}
        <View wrap={false}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>Red Flags, If any:</Text>
          {safe.red_alert.map((r, i) => (
            <Text key={i}>
              {r.redAlert}
              {r.remark ? ` - ${r.remark}` : ""}
            </Text>
          ))}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 5. PRESENTING COMPLAINTS (LOCKED)                     */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
        <View style={styles.horizontalLine} />
          <Text style={styles.label}>Presenting Complaints:</Text>
          {/* <View style={styles.box}> */}
            <Text>{renderHtmlToPdf(safe.pms_data?.principal_complaints)}</Text>
          {/* </View> */}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 6. TREATMENT HISTORY (LOCKED)                         */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
        <View style={styles.horizontalLine} />
          <Text style={styles.label}>Treatment History:</Text>
          {/* <View style={styles.box}> */}
            <Text>{renderHtmlToPdf(safe.pms_data?.pain_treatment_history)}</Text>
          {/* </View> */}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 7. HEALTH CONDITIONS (LOCKED)                         */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>Health Conditions:</Text>
          {safe.pms_data?.health_condition?.map((h, i) => (
            <Text key={i}>
              {h.healthCondition}
              {h.remark ? ` - ${h.remark}` : ""}
            </Text>
          ))}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 8. CURRENT MEDICATIONS (LOCKED)                       */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>Current Medications:</Text>
          {safe.medication_data.map((m, i) => (
            <Text key={i}>
              {m.current_medication}
              {m.remark ? ` - ${m.remark}` : ""}
            </Text>
          ))}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 9. DRUG ALLERGIES (LOCKED)                            */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
        <View style={styles.horizontalLine} />
          <Text style={styles.label}>Drug Allergies, if any:</Text>
          {safe.pms_data?.drug_allergies?.map((a, i) => (
            <Text key={i}>
              {a.data}
              {a.remark ? ` - ${a.remark}` : ""}
            </Text>
          ))}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 10. PREVIOUS INVESTIGATION REPORT (LOCKED)            */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
        <View style={styles.horizontalLine} />
          <Text style={styles.label}>Previous Investigation Report:</Text>
          {/* <View style={styles.box}> */}
            <Text>{renderHtmlToPdf(safe.pms_data?.previous_investigation_report)}</Text>
          {/* </View> */}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 11. INVESTIGATIONS PRESCRIBED (LOCKED)                */}
        {/* ------------------------------------------------------ */}
        {(safe.investigation_data?.length > 0 || safe.imaging_data?.length > 0) && (
        <View wrap={false} style={{ marginTop: 10 }}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>Investigations Prescribed:</Text>

          {safe.investigation_data.map((inv, i) => (
            <Text key={i}>
              {inv?.test || inv?.investigationtests}
              {inv.remark ? ` - ${inv.remark}` : ""}
            </Text>
          ))}

          {safe.imaging_data.map((img, i) => (
            <Text key={i}>
              {img?.image || img?.investigationimaging}
              {img.remark ? ` - ${img.remark}` : ""}
            </Text>
          ))}
        </View>
        )}

        {/* ------------------------------------------------------ */}
        {/* 13. TREATMENT / ADVICE (LOCKED)                       */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>Rx : Treatment / Advice</Text>

          {safe.treatment_procedure_data.map((t, i) => (
            <Text key={i}>
              {t?.treatment || t?.treatmentprocedure}
              {t.remark ? ` - ${t.remark}` : ""}
            </Text>
          ))}

          {safe.treatment_other_data.map((t, i) => (
            <Text key={i}>
              {t?.treatment || t?.treatmentother}
              {t.remark ? ` - ${t.remark}` : ""}
            </Text>
          ))}
        </View>

        {/* ------------------------------------------------------ */}
        {/* 14. MEDICINES TABLE (LOCKED - NEVER BREAK)            */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>Medicines</Text>

          <View style={styles.table} wrap={false}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, { flex: 0.6 }, styles.bold]}>S. No.</Text>
              <Text style={[styles.tableCell, { flex: 2 }, styles.bold]}>Medication</Text>
              <Text style={[styles.tableCell, { flex: 1.2 }, styles.bold]}>Regimen</Text>
              <Text style={[styles.tableCell, { flex: 1.5 }, styles.bold]}>Meal</Text>
              <Text style={[styles.tableCell, { flex: 1.5 }, styles.bold]}>Duration</Text>
              <Text style={[styles.tableCell, { flex: 2 }, styles.bold]}>Remark</Text>
            </View>

            {safe.treatment_medication.map((item, i) => (
              <View
                key={i}
                style={[
                  styles.tableRow,
                  i % 2 !== 0 ? styles.stripedRow : null,
                ]}
              >
                <Text style={[styles.tableCell, { flex: 0.6 }]}>{i + 1}</Text>
                <View style={[styles.tableCell, { flex: 2 }]}>
                  <Text>{item.medication}</Text>
                  {item.shorter_composition ? <Text style={{ fontSize: 7, color: '#666', fontStyle: 'italic', marginTop: 2 }}>{item.shorter_composition}</Text> : null}
                </View>
                <Text style={[styles.tableCell, { flex: 1.2 }]}>{item.regimen == 'Other' ? item?.other_regimen : item.regimen}</Text>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>{item.meal}</Text>
                <Text style={[styles.tableCell, { flex: 1.5 }]}>{item.duration}</Text>
                <Text style={[styles.tableCell, { flex: 2 }]}>{item.remark}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ------------------------------------------------------ */}
        {/* 15. ADDITIONAL NOTE (LOCKED)                          */}
        {/* ------------------------------------------------------ */}
        <View wrap={false} style={{ marginTop: 10 }}>
          <View style={styles.horizontalLine} />
          <Text style={styles.label}>Additional Note:</Text>
          <Text>{renderHtmlToPdf(safe.pms_data?.additional_note)}</Text>
        </View>

        {/* ------------------------------------------------------ */}
        {/* 16. DOCTOR SIGNATURE (OPTION B — NOT FORCED)          */}
        {/* ------------------------------------------------------ */}
        
        {safe?.clinic_meta_data?.footer === "mss" ? (
            <View style={styles.followupRow}>
            <View style={styles.followupLeft}>
              <Text style={styles.doctorName}>{safe.doctor_name}</Text>
              <Text style={styles.doctorDetails}>{safe.doctor_degree}</Text>
              <Text style={styles.doctorDetails}>Sr. Pain Management Specialist</Text>
              {/* <Text style={styles.followupLabel}>Next Check-in Date : {safe.next_checkin_date ? formatDate(safe.next_checkin_date) : ""}</Text> */}
            </View>
  
            <View style={{ width: "40%", alignItems: "flex-end" }}>
              
                <Image
                  src={"/Nivaan_logo.png"}
                  style={{
                    width: 140,
                    height: "auto",
                  }}
                />
               
            </View>
          </View>
  
        ) : (
          <View style={styles.followupRow}>
          <View style={styles.followupLeft}>
            <Text style={styles.followupLabel}>Next Check-in Date</Text>
            <Text style={styles.followupDate}>
              {safe.next_checkin_date ? formatDate(safe.next_checkin_date) : ""}
            </Text>
          </View>

          <View style={styles.followupRight}>
            <Text style={styles.doctorName}>{safe.doctor_name}</Text>
            <Text style={styles.doctorDetails}>{safe.doctor_degree}</Text>
            <Text style={styles.doctorDetails}>Sr. Pain Management Specialist</Text>
            <Text style={styles.doctorDetails}>Reg. No. · {safe.license_number}</Text>
            <Text style={styles.signatureText}>Signature</Text>
          </View>
        </View>

        
        )}

        
        {/* ---------------- FOOTER (FIXED) ---------------- */}
        
        {useCustomHeaderFooter ? (
          <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 80 }} fixed>
            <Image 
              style={{ width: '100%', height: '100%' }} 
              src={customFooterUrl} 
            />
          </View>
        ) : safe?.clinic_meta_data?.footer === "mss" ? (
          <View style={styles.footer_white} fixed>
          <View
            style={{
              width: "100%",
              textAlign: "center",
              paddingHorizontal: 10,
            }}
          >
            {/* MAIN TITLE */}
            <Text
              style={{
                textAlign: "center",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 1,
                marginBottom: 6,
                color: "#444",
              }}
            >
              MINIMAL ACCESS SMART SURGERY HOSPITALS PRIVATE LIMITED
            </Text>
            {/* GREY LINE */}
            <View
              style={{
                borderBottomColor: "#999",
                borderBottomWidth: 1,
                marginBottom: 8,
              }}
            />
        
            {/* INFO ROW */}
           
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* ADDRESS */}
                <View style={{ flexDirection: "row", alignItems: "center", width: "25%" }}>
                  <Text style={{ color: "#0C8A77", fontSize: 18 }}>■ </Text>
                  <Text style={{ fontSize: 9 }}>
                    B-18, Chirag Enclave, Nehru Place, New Delhi-110048
                  </Text>
                </View>

                {/* EMAIL */}
                <View style={{ flexDirection: "row", alignItems: "center", width: "25%", justifyContent: "center" }}>
                  <Text style={{ color: "#0C8A77", fontSize: 18 }}>■ </Text>
                  <Text style={{ fontSize: 9 }}>info@massh.in</Text>
                </View>

                {/* PHONE */}
                <View style={{ flexDirection: "row", alignItems: "center", width: "25%", justifyContent: "center" }}>
                  <Text style={{ color: "#0C8A77", fontSize: 18 }}>■ </Text>
                  <Text style={{ fontSize: 9 }}>011-35021666</Text>
                </View>

                {/* WEBSITE */}
                <View style={{ flexDirection: "row", alignItems: "center", width: "25%", justifyContent: "flex-end" }}>
                  <Text style={{ color: "#0C8A77", fontSize: 18 }}>■ </Text>
                  <Text style={{ fontSize: 9 }}>www.massh.in</Text>
                </View>
              </View>
            </View>
          </View>
        ) : (
          <View style={styles.footer} fixed>
            <View style={styles.footerRow}>
              {/* PAGE NUMBER */}
              <View style={styles.footerSmallCol}>
                <Text
                  render={({ pageNumber, totalPages }) =>
                    `${pageNumber} of ${totalPages}`
                  }
                />
              </View>

              {/* CONTACT INFO */}
              <View style={styles.footerColumn}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <Text>For Appointment </Text>
                  <Image style={styles.footer_logo} src="/call.png" />
                  <Text> - {safe.cm_contact}</Text>
                </View>
                <Text style={styles.centerText}>{safe.cm_address}</Text>
              </View>

              {/* WEBSITE */}
              <View style={[styles.footerColumn, { alignItems: "flex-end" }]}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <Image style={styles.footer_logo} src="/website.png" />
                  <Text style={{ marginLeft: 4 }}>www.nivaancare.com</Text>
                </View>
              </View>
            </View>
            </View>
          )}
          
      </Page>
    </Document>
  );
};

export default OPDReportPDF;